import React from 'react'
import { useIntl } from 'react-intl'

import { SectionCurve } from '../../../components/SectionCurve'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useRoutes } from '../../../routes'
import { SectionTestimonials } from '../../home/SectionTestimonials'
import { Section3CardsBenefits } from '../Section3CardsBenefits'
import { SectionGridBenefits } from '../SectionGridBenefits'
import { SectionHero } from '../SectionHero'
import { SectionQuote } from '../SectionQuote'
import { SectionSwitchedBenefits } from '../SectionSwitchedBenefits'
import { SectionTable } from '../SectionTable'

export default function CompareToConfluencePage() {
  const routes = useRoutes()
  const intl = useIntl()

  const title = intl.formatMessage({
    defaultMessage:
      'Slite - a modern Confluence alternative for your entire company.',
    id: 'compare.confluence.page.seo.title',
  })

  const description = intl.formatMessage({
    defaultMessage: `Slite is the AI-powered knowledge base that enables fast-growing teams to effortlessly create, find and manage their company knowledge at scale. Import your Confluence docs and try it for free today.`,
    id: 'compare.confluence.page.seo.description',
  })

  return (
    <SlitePage
      canonicalUrl={routes.compare.detail({
        slug: 'alternative-to-confluence',
      })}
      title={title}
      description={description}
      background="moon-dust"
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/ec0ced21d1b39167d3904a289ab5f2cc1e0cb0e9-2100x1200.webp"
    >
      <SectionHero
        competitorName="Confluence"
        title={intl.formatMessage({
          defaultMessage:
            'Modern Confluence alternative for your entire company, not just tech team.',
          id: 'compare.confluence.page.title',
        })}
        subtitle={intl.formatMessage({
          defaultMessage:
            'Confluence got engineering teams into the habit of documenting, but it has never been adopted company-wide. Slite makes documentation actually enjoyable for the whole team, finally breaking the siloes and making a single source of truth a reality.',
          id: 'compare.confluence.page.subtitle',
        })}
      />

      <SectionCurve from="moon-dust" to="shade-100" shape="top-large" />

      <Section3CardsBenefits
        title={intl.formatMessage({
          defaultMessage: 'Why are teams switching from Confluence to Slite?',
          id: 'compare.confluence.featureSection.title',
        })}
        benefits={[
          {
            imageUrl:
              'https://cdn.prod.website-files.com/5f84417443f34a30444f52d3/658eafb4b3102c387ddcd7d6_product-get-answers.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Instant answers illustration',
              id: 'compare.confluence.feature1.image.alt',
            }),
            imageBackground: 'blue-50',
            title: intl.formatMessage({
              defaultMessage: 'All answers, in an instant.',
              id: 'compare.confluence.feature1.title',
            }),
            description: intl.formatMessage({
              id: 'compare.confluence.feature1.description',
              defaultMessage:
                "Confluence search is slow and often gives unreliable results. With Slite's AI assistant Ask, you get immediate trusted answers by just asking questions.",
            }),
          },
          {
            imageUrl:
              'https://cdn.prod.website-files.com/5f84417443f34a30444f52d3/658d34220fe960c2d86a604c_product-knowledge-management.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Knowledge maintenance illustration',
              id: 'compare.confluence.feature2.image.alt',
            }),
            imageBackground: 'blue-45',
            title: intl.formatMessage({
              defaultMessage: 'Effortless maintenance.',
              id: 'compare.confluence.feature2.title',
            }),
            description: intl.formatMessage({
              id: 'compare.confluence.feature2.description',
              defaultMessage:
                "Many teams using Confluence complain about the clutter of outdated docs that they can't trust. Slite gives you tools to verify your docs and maintain your knowledge base fresh, at scale.",
            }),
          },
          {
            imageUrl:
              'https://cdn.prod.website-files.com/5f84417443f34a30444f52d3/658d342346a2dae052484f7c_product-collaborative-cursors.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Company-wide adoption illustration',
              id: 'compare.confluence.feature3.image.alt',
            }),
            imageBackground: 'primary',
            title: intl.formatMessage({
              defaultMessage: 'Loved company-wide.',
              id: 'compare.confluence.feature3.title',
            }),
            description: intl.formatMessage({
              id: 'compare.confluence.feature3.description',
              defaultMessage:
                'Confluence is known as complex and over-engineered, especially for the entire team. Slite is intuitive and user-friendly for everyone, ensuring a low learning curve and high engagement.',
            }),
          },
        ]}
      />

      <SectionGridBenefits
        title={intl.formatMessage({
          defaultMessage:
            'Get instant trusted answers, where and when you need them.',
          id: 'compare.confluence.benefitsSection.title',
        })}
        data={[
          {
            title: intl.formatMessage({
              defaultMessage: 'Can I really just ask instead of search?',
              id: 'compare.confluence.grid.benefit1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Endless searching and browsing is a thing of the past. Thanks to AI, now you can just ask questions in your natural language and have instant answers based on your docs.',
              id: 'compare.confluence.grid.benefit1.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/7026c497c3a256414ba10a092aec0e19ba328dba-1072x665.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Ask instead of search illustration',
              id: 'compare.confluence.grid.benefit1.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'Wait, is it possible to ask the questions where I already work?',
              id: 'compare.confluence.grid.benefit2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'No more context or apps switching - stay in your workflow and get trusted answers in Slack or while you browse with Chrome extension.',
              id: 'compare.confluence.grid.benefit2.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/9f2ce8d23d5caafbedcacc0ececb057ffd4ba0ff-2144x1328.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Apps and integrations illustration',
              id: 'compare.confluence.grid.benefit2.image.alt',
            }),
            imageClassnames:
              'padding-horizontal-24 margin-bottom-24 radius-larger overflow-hidden',
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'Yes, but how do I know if I can trust the answers?',
              id: 'compare.confluence.grid.benefit3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "Filter answers based only on verified sources - this way you are always sure that it's trustworthy.",
              id: 'compare.confluence.grid.benefit3.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/064bfb97c2427edeaf91146ae0bd1957bbb297a2-2144x1748.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Verified sources illustration',
              id: 'compare.confluence.grid.benefit3.image.alt',
            }),
            imageClassnames: '',
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'But the knowledge is scattered all over the tools...',
              id: 'compare.confluence.grid.benefit4.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "Exactly - your company knowledge is so much more than what's in your knowledge base. That's why now you can connect other tools to Slite and get answers on all the knowledge you know is out somewhere.",
              id: 'compare.confluence.grid.benefit4.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/569a7178bcccf8f49b9775eae3c5e4a6fe7e1a78-1072x665.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Connected tools illustration',
              id: 'compare.confluence.grid.benefit4.image.alt',
            }),
            imageClassnames:
              'padding-horizontal-24 flexbox flexbox-justify-end',
          },
        ]}
      />

      <SectionCurve from="shade-100" to="pink-moon-darker" shape="top-large" />
      <SectionQuote
        background="pink-moon-darker"
        quote={intl.formatMessage({
          defaultMessage: `"The best thing about Slite is it's clutter free, clean interface. I was a power user of Confluence for the last 5 years, but it's just too much! Slite on the other hand is lean and well designed. It has minimal navigation, has all the features that I need."`,
          id: 'compare.confluence.quote.deepak.text',
        })}
        authorImageUrl="https://cdn.prod.website-files.com/5f84417443f34a30444f52d3/647cd661d3cfe3869a45438c_Deepak%20choudhary.webp"
        authorTitle="Deepak Choudhary"
        authorDescription="WE-R"
      />
      <SectionCurve
        from="moon-dust"
        to="pink-moon-darker"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />

      <SectionSwitchedBenefits
        title={intl.formatMessage({
          defaultMessage: 'Sounds great, but I have a few questions...',
          id: 'compare.confluence.faqSection.title',
        })}
        data={[
          {
            title: intl.formatMessage({
              defaultMessage:
                'What if our team still wants to use Jira for task management?',
              id: 'compare.confluence.faq1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'No worries at all – stick with your go-to tools, and Slite ensures seamless integration with them. With our Jira integration, effortlessly insert Jira issues into your Slite docs. The tile content remains consistently updated, ensuring you and your team stay in the loop.',
              id: 'compare.confluence.faq1.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/21ad8f4c53d4f2d60cd1f083ea97442186d7603f-1396x722.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Jira integration illustration',
              id: 'compare.confluence.faq1.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                "It's hard to keep content up-to-date in Confluence. Do you offer anything to improve this?",
              id: 'compare.confluence.faq2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Slite is built for team knowledge management, providing you with tools to effortlessly keep company knowledge relevant and up-to-date. Easily slice and dice all your docs, take AI-suggested actions and act on them in bulk - all within one central panel.',
              id: 'compare.confluence.faq2.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/5836e6cd2443fd468969ef629fc94af709b40b6a-1396x722.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Knowledge management illustration',
              id: 'compare.confluence.faq2.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'The editor in Confluence is very limited. How is it in Slite?',
              id: 'compare.confluence.faq3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'We know, documenting in Confluence often feels like a tedious task. Slite makes it easy, even for non-techies. Our AI-powered editor is fast, smooth, and never gets in your way. It comes with a variety of built-in formatting options and AI Assistant that helps to improve your docs with just a click.',
              id: 'compare.confluence.faq3.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/edf15dbdafa7e8b3dcd3ea06fd0e2026ac6c6857-1396x722.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Editor illustration',
              id: 'compare.confluence.faq3.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                "Will switching to Slite end my team's complaints about the tool being too technical?",
              id: 'compare.confluence.faq4.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "It will, at last! While Confluence claims to be suitable for all kinds of teams, like marketing or HR, it often scares off users with its technical approach. Slite is highly rated for its easiness of use on G2 and other platforms. That's why most Slite customers use it throughout their whole company, not just one team.",
              id: 'compare.confluence.faq4.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/56fec22c648387a8d256b1f7da99ff55566a3492-1396x722.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Easy for everyone illustration',
              id: 'compare.confluence.faq4.image.alt',
            }),
          },
        ]}
      />

      <SectionCurve from="moon-dust" to="shade-00" shape="top-large" />
      <SectionTable
        competitorName="Confluence"
        title={intl.formatMessage({
          defaultMessage: 'Powerful, but too complex for your whole company.',
          id: 'compare.confluence.comparisonTable.title',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Confluence has no verification, no content health tools, and AI is locked behind Rovo or Premium. Slite gives every team AI answers, knowledge management, and MCP - without the complexity.',
          id: 'compare.confluence.comparisonTable.description.v2',
        })}
        checkedFeatures={[
          'company-wiki-templates',
          'mcp-integration',
          'enterprise-search',
          'ai-workflows',
        ]}
        hiddenFeatures={['ai-workflows']}
      />
      <SectionCurve
        from="moon-dust"
        to="shade-00"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />

      <SectionTestimonials withCaseStudies={false} />
      <SectionLastCTA background="moon-dust" competitorName="Confluence" />
    </SlitePage>
  )
}
